import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { Download, Printer, LineChart as LineIcon, AlertTriangle, Search, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { downloadXlsx } from "@/lib/export";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import PettyCashTab from "./PettyCashTab";
import DailyReportTab from "./DailyReportTab";
import TransactionCorrectionsTab from "./TransactionCorrectionsTab";

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get("tab") || "pnl");
  // v31 — keep URL ?tab= in sync so Dashboard "Petty Cash" card lands here
  useEffect(() => {
    if (tab !== (searchParams.get("tab") || "pnl")) {
      setSearchParams({ tab }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Analytics</div>
          <h1 className="font-display text-4xl font-bold mt-1">Reports & P&amp;L</h1>
          <p className="text-muted-foreground mt-1">
            Financial snapshots, outstanding dues, and subscription expiry tracking.
          </p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <TabBtn active={tab === "pnl"} onClick={() => setTab("pnl")} tid="tab-pnl">Profit &amp; Loss</TabBtn>
        <TabBtn active={tab === "pnl-module"} onClick={() => setTab("pnl-module")} tid="tab-pnl-module">P&amp;L by Module</TabBtn>
        <TabBtn active={tab === "outstanding"} onClick={() => setTab("outstanding")} tid="tab-outstanding">Outstanding Balance</TabBtn>
        <TabBtn active={tab === "expiry"} onClick={() => setTab("expiry")} tid="tab-expiry">Subscription Expiry</TabBtn>
        <TabBtn active={tab === "petty-cash"} onClick={() => setTab("petty-cash")} tid="tab-petty-cash">Petty Cash</TabBtn>
        <TabBtn active={tab === "daily-report"} onClick={() => setTab("daily-report")} tid="tab-daily-report">Daily Report</TabBtn>
        <TabBtn active={tab === "txn-corrections"} onClick={() => setTab("txn-corrections")} tid="tab-txn-corrections">Transaction Corrections</TabBtn>
      </div>

      {tab === "pnl" && <PnlTab />}
      {tab === "pnl-module" && <PnlByModuleTab />}
      {tab === "outstanding" && <OutstandingTab />}
      {tab === "expiry" && <ExpiryTab />}
      {tab === "petty-cash" && <PettyCashTab />}
      {tab === "daily-report" && <DailyReportTab />}
      {tab === "txn-corrections" && <TransactionCorrectionsTab />}
    </div>
  );
}

function PnlByModuleTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState({ rows: [], totals: {} });
  useEffect(() => {
    api.get(`/reports/pnl-by-module?period=${period}`).then((r) => setData(r.data)).catch(() => {});
  }, [period]);
  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="pnl-module-tab">
      <div className="flex justify-between flex-wrap gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Monthly</SelectItem>
            <SelectItem value="quarter">Quarterly</SelectItem>
            <SelectItem value="year">Yearly</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline"
          onClick={() => downloadXlsx("pnl-by-module").catch(() => toast.error("Export failed"))}
          data-testid="export-pnl-module">
          <Download className="w-4 h-4 mr-2" /> Excel
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Residence Income" amount={t.residence_income} accent="text-emerald-700 bg-emerald-50 border-emerald-200" />
        <MetricCard label="Mess Income" amount={t.mess_income} accent="text-amber-700 bg-amber-50 border-amber-200" />
        <MetricCard label="Total Expense" amount={t.expense} accent="text-rose-700 bg-rose-50 border-rose-200" />
        <MetricCard label="Net Profit" amount={t.net_profit} accent="text-primary bg-primary/5 border-primary/30" />
      </div>
      {/* v28.1 — Expenses split by module so Hostel vs Mess P&L is clean */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pnl-module-expense-breakdown">
        <MetricCard label="Hostel Expenses" amount={t.hostel_expense} accent="text-rose-700 bg-rose-50 border-rose-200" />
        <MetricCard label="Mess Expenses" amount={t.mess_expense} accent="text-rose-700 bg-rose-50 border-rose-200" />
        <MetricCard label="Other / General Expenses" amount={t.other_expense} accent="text-rose-700 bg-rose-50 border-rose-200" />
        <MetricCard label="Hostel Net · Mess Net" amount={`${Number(t.residence_net||0).toLocaleString("en-IN")} · ${Number(t.mess_net||0).toLocaleString("en-IN")}`} accent="text-primary bg-primary/5 border-primary/30" />
      </div>
      <Card className="p-0 border-border bg-white overflow-hidden">
        <div className="h-72 p-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.rows}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="residence_income" name="Residence" fill="#0B4A3F" />
              <Bar dataKey="mess_income" name="Mess" fill="#8E5A2A" />
              <Bar dataKey="expense" name="Expense" fill="#B94F4F" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 px-4">Period</th>
                <th className="py-2 pr-4 text-right">Residence</th>
                <th className="py-2 pr-4 text-right">Mess</th>
                <th className="py-2 pr-4 text-right">Total Income</th>
                <th className="py-2 pr-4 text-right">Expense</th>
                <th className="py-2 pr-4 text-right">Net Profit</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.period} className="border-b border-border/50 hover:bg-secondary/40"
                  data-testid={`pnl-module-row-${r.period}`}>
                  <td className="py-2 px-4 font-mono text-xs">{r.period}</td>
                  <td className="py-2 pr-4 text-right font-mono">₹{r.residence_income.toLocaleString("en-IN")}</td>
                  <td className="py-2 pr-4 text-right font-mono">₹{r.mess_income.toLocaleString("en-IN")}</td>
                  <td className="py-2 pr-4 text-right font-mono">₹{r.total_income.toLocaleString("en-IN")}</td>
                  <td className="py-2 pr-4 text-right font-mono text-rose-700">₹{r.expense.toLocaleString("en-IN")}</td>
                  <td className="py-2 pr-4 text-right font-mono font-bold">₹{r.net_profit.toLocaleString("en-IN")}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function MetricCard({ label, amount, accent = "" }) {
  const isNum = typeof amount === "number" || amount == null;
  return (
    <Card className={`p-4 border ${accent || "border-border bg-white"}`}>
      <div className="eyebrow text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">
        {isNum ? `₹${Number(amount || 0).toLocaleString("en-IN")}` : `₹${amount}`}
      </div>
    </Card>
  );
}

const TabBtn = ({ active, children, onClick, tid }) => (
  <button
    onClick={onClick}
    data-testid={tid}
    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
      active ? "bg-primary text-primary-foreground" : "bg-white border border-border hover:bg-secondary"
    }`}
  >
    {children}
  </button>
);

// ============ P&L Tab ============
// v36 — Date-driven filter modes: specific date · range · month · quarter · year.
function pnlComputeRange(mode, s) {
  // Returns { period, date_from, date_to } to pass to the API.
  const pad = (n) => String(n).padStart(2, "0");
  if (mode === "date") {
    const d = s.date || new Date().toISOString().slice(0, 10);
    return { period: "day", date_from: d, date_to: d };
  }
  if (mode === "range") {
    return { period: "day", date_from: s.from || "", date_to: s.to || "" };
  }
  if (mode === "month") {
    const [y, m] = (s.month || new Date().toISOString().slice(0, 7)).split("-");
    const last = new Date(Number(y), Number(m), 0).getDate();
    return { period: "month", date_from: `${y}-${pad(m)}-01`, date_to: `${y}-${pad(m)}-${pad(last)}` };
  }
  if (mode === "quarter") {
    const y = s.year || String(new Date().getFullYear());
    const q = Number(s.quarter || "1");
    const startMonth = (q - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    const last = new Date(Number(y), endMonth, 0).getDate();
    return { period: "quarter", date_from: `${y}-${pad(startMonth)}-01`, date_to: `${y}-${pad(endMonth)}-${pad(last)}` };
  }
  if (mode === "year") {
    const y = s.year || String(new Date().getFullYear());
    return { period: "year", date_from: `${y}-01-01`, date_to: `${y}-12-31` };
  }
  // Fallback — no filter, monthly aggregation
  return { period: "month", date_from: "", date_to: "" };
}

function PnlDateFilter({ mode, setMode, sel, setSel }) {
  const nowY = String(new Date().getFullYear());
  const currMonth = new Date().toISOString().slice(0, 7);
  const today = new Date().toISOString().slice(0, 10);
  const years = Array.from({ length: 6 }, (_, i) => String(Number(nowY) - i));
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div>
        <div className="eyebrow text-muted-foreground mb-1">Filter</div>
        <Select value={mode} onValueChange={(v) => { setMode(v); setSel({}); }}>
          <SelectTrigger className="w-[170px]" data-testid="pnl-filter-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="quarter">Quarter</SelectItem>
            <SelectItem value="year">Year</SelectItem>
            <SelectItem value="date">Specific Date</SelectItem>
            <SelectItem value="range">Date Range</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mode === "date" && (
        <div>
          <div className="eyebrow text-muted-foreground mb-1">Date</div>
          <Input type="date" value={sel.date || today}
            onChange={(e) => setSel({ ...sel, date: e.target.value })}
            className="w-[170px]" data-testid="pnl-filter-date" />
        </div>
      )}

      {mode === "range" && (
        <>
          <div>
            <div className="eyebrow text-muted-foreground mb-1">From</div>
            <Input type="date" value={sel.from || ""}
              onChange={(e) => setSel({ ...sel, from: e.target.value })}
              className="w-[170px]" data-testid="pnl-filter-from" />
          </div>
          <div>
            <div className="eyebrow text-muted-foreground mb-1">To</div>
            <Input type="date" value={sel.to || ""}
              onChange={(e) => setSel({ ...sel, to: e.target.value })}
              className="w-[170px]" data-testid="pnl-filter-to" />
          </div>
        </>
      )}

      {mode === "month" && (
        <div>
          <div className="eyebrow text-muted-foreground mb-1">Month</div>
          <Input type="month" value={sel.month || currMonth}
            onChange={(e) => setSel({ ...sel, month: e.target.value })}
            className="w-[180px]" data-testid="pnl-filter-month" />
        </div>
      )}

      {mode === "quarter" && (
        <>
          <div>
            <div className="eyebrow text-muted-foreground mb-1">Year</div>
            <Select value={sel.year || nowY} onValueChange={(v) => setSel({ ...sel, year: v })}>
              <SelectTrigger className="w-[120px]" data-testid="pnl-filter-qyear"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <div className="eyebrow text-muted-foreground mb-1">Quarter</div>
            <Select value={sel.quarter || "1"} onValueChange={(v) => setSel({ ...sel, quarter: v })}>
              <SelectTrigger className="w-[130px]" data-testid="pnl-filter-quarter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1 (Jan–Mar)</SelectItem>
                <SelectItem value="2">Q2 (Apr–Jun)</SelectItem>
                <SelectItem value="3">Q3 (Jul–Sep)</SelectItem>
                <SelectItem value="4">Q4 (Oct–Dec)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {mode === "year" && (
        <div>
          <div className="eyebrow text-muted-foreground mb-1">Year</div>
          <Select value={sel.year || nowY} onValueChange={(v) => setSel({ ...sel, year: v })}>
            <SelectTrigger className="w-[120px]" data-testid="pnl-filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

function PnlTab() {
  const [mode, setMode] = useState("month");
  const [sel, setSel] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { period, date_from, date_to } = pnlComputeRange(mode, sel);
    // Guard: range mode needs both from + to before hitting the API
    if (mode === "range" && (!date_from || !date_to)) { setData(null); return; }
    setLoading(true);
    const qs = new URLSearchParams({ period });
    if (date_from) qs.set("date_from", date_from);
    if (date_to)   qs.set("date_to",   date_to);
    api.get(`/reports/pnl?${qs.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load P&L"))
      .finally(() => setLoading(false));
  }, [mode, sel]);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const rangeLabel = (() => {
    const r = pnlComputeRange(mode, sel);
    if (!r.date_from && !r.date_to) return null;
    if (r.date_from === r.date_to) return r.date_from;
    return `${r.date_from || "…"} → ${r.date_to || "…"}`;
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <PnlDateFilter mode={mode} setMode={setMode} sel={sel} setSel={setSel} />
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-pnl">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button variant="outline" onClick={() => downloadXlsx("pnl").catch(() => toast.error("Export failed"))}
                  data-testid="export-pnl">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      {rangeLabel && (
        <div className="text-xs text-muted-foreground" data-testid="pnl-range-label">
          Showing transactions {rangeLabel}{loading ? " · loading…" : ""}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SummaryCard label="Total Income" value={fmt(data?.total_income)} tone="primary" />
        <SummaryCard label="Total Expenses" value={fmt(data?.total_expense)} tone="warn" />
        <SummaryCard label="Net Profit / Loss" value={fmt(data?.net_profit)}
                     tone={data?.net_profit >= 0 ? "accent" : "warn"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pnl-income-breakdown">
        <SummaryCard label="Hostel Income" value={fmt(data?.hostel_income)} tone="accent" />
        <SummaryCard label="Mess Income" value={fmt(data?.mess_income)} tone="accent" />
        <SummaryCard label="Other Income" value={fmt(Math.max(0, (data?.total_income || 0) - (data?.hostel_income || 0) - (data?.mess_income || 0)))} tone="accent" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="pnl-expense-breakdown">
        <SummaryCard label="Hostel Expenses" value={fmt(data?.hostel_expense)} tone="warn" />
        <SummaryCard label="Mess Expenses" value={fmt(data?.mess_expense)} tone="warn" />
        <SummaryCard label="Other / General Expenses" value={fmt(data?.other_expense)} tone="warn" />
        <SummaryCard label="Petty Cash (in period)" value={fmt(data?.petty_cash)} tone="warn" />
      </div>

      <Card className="p-6 border-border bg-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="eyebrow text-muted-foreground">Trend</div>
            <div className="font-display text-2xl font-bold mt-1">Income vs Expenses</div>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.rows || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1E7DD" />
              <XAxis dataKey="period" stroke="#5B6B62" fontSize={12} />
              <YAxis stroke="#5B6B62" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0B4A3F", border: "none", color: "#F7F9F6", borderRadius: 6, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="income" name="Income" fill="#0B4A3F" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expense" name="Expense" fill="#C45B2D" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="pnl-table">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-3 pr-4">Period</th>
                <th className="py-3 pr-4 text-right">Income</th>
                <th className="py-3 pr-4 text-right">Expense</th>
                <th className="py-3 pr-4 text-right">Petty Cash</th>
                <th className="py-3 pr-4 text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r) => (
                <tr key={r.period} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-mono">{r.period}</td>
                  <td className="py-2 pr-4 text-right font-mono">{fmt(r.income)}</td>
                  <td className="py-2 pr-4 text-right font-mono">{fmt(r.expense)}</td>
                  <td className="py-2 pr-4 text-right font-mono">{fmt(r.petty_cash)}</td>
                  <td className="py-2 pr-4 text-right font-mono font-semibold">
                    <span className={r.profit >= 0 ? "" : "text-destructive"}>{fmt(r.profit)}</span>
                  </td>
                </tr>
              ))}
              {(!data || data.rows.length === 0) && (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">
                  {loading ? "Loading…" : "No transactions in this period."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

const SummaryCard = ({ label, value, tone }) => (
  <Card className="p-5 border-border bg-white">
    <div className="eyebrow text-muted-foreground">{label}</div>
    <div className={`font-display text-3xl font-bold mt-2 ${
      tone === "warn" ? "text-destructive" : tone === "accent" ? "text-accent" : ""
    }`}>{value}</div>
  </Card>
);

// ============ Outstanding Tab ============
function OutstandingTab() {
  const { user } = useAuth();
  const canDeleteFee = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);     // resident_id currently expanded
  const [deletingFee, setDeletingFee] = useState(null); // fee detail object pending confirm
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reports/outstanding").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    const t = q.toLowerCase();
    return !t || [r.name, r.phone, r.company, r.room_no]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(t));
  });
  const total = filtered.reduce((s, r) => s + r.balance, 0);

  // v42 — Soft-delete (archive) an incorrect/outdated outstanding fee. Backend
  // excludes archived fees from this report, the ledger, Dashboard dues and
  // every fee list; linked payments are preserved untouched.
  const confirmDeleteFee = async () => {
    if (!deletingFee) return;
    setBusy(true);
    try {
      await api.delete(`/fees/${deletingFee.id}`);
      toast.success("Outstanding fee deleted — dues, dashboard and ledger recalculated.");
      setDeletingFee(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by name, room..." className="pl-9"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="outstanding-search" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button variant="outline" onClick={() => downloadXlsx("outstanding").catch(() => toast.error("Export failed"))}
                  data-testid="export-outstanding">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
        </div>
      </div>

      <Card className="p-4 border-border bg-white">
        <div className="flex items-center justify-between mb-3 text-sm">
          <div className="text-muted-foreground">{filtered.length} residents with dues</div>
          <div className="font-display font-bold">Total: ₹{total.toLocaleString("en-IN")}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="outstanding-table">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-3 pr-2"></th>
                <th className="py-3 pr-4">Resident</th>
                <th className="py-3 pr-4">Room</th>
                <th className="py-3 pr-4 text-right">Billed</th>
                <th className="py-3 pr-4 text-right">Paid</th>
                <th className="py-3 pr-4 text-right">Balance</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isOpen = expanded === r.resident_id;
                const detail = r.fees_detail || [];
                return (
                  <>
                    <tr key={r.resident_id} className="border-b border-border/50">
                      <td className="py-3 pr-2">
                        <button
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => setExpanded(isOpen ? null : r.resident_id)}
                          data-testid={`expand-dues-${r.resident_id}`}
                          aria-label="Show outstanding fee entries">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="py-3 pr-4 font-medium">{r.name}</td>
                      <td className="py-3 pr-4 font-mono">{r.room_no || "—"}</td>
                      <td className="py-3 pr-4 text-right font-mono">₹{r.total_billed.toLocaleString("en-IN")}</td>
                      <td className="py-3 pr-4 text-right font-mono">₹{r.total_paid.toLocaleString("en-IN")}</td>
                      <td className="py-3 pr-4 text-right font-mono font-semibold text-destructive">
                        ₹{r.balance.toLocaleString("en-IN")}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <Link to={`/residents/${r.resident_id}/ledger`}>
                          <Button size="sm" variant="outline" data-testid={`ledger-${r.resident_id}`}>
                            Ledger
                          </Button>
                        </Link>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${r.resident_id}-detail`} className="bg-secondary/30">
                        <td colSpan={9} className="py-3 px-4">
                          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                            Outstanding fee entries ({detail.length})
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-muted-foreground border-b border-border/50">
                                <th className="py-1 pr-3">Invoice</th>
                                <th className="py-1 pr-3">Module</th>
                                <th className="py-1 pr-3">Period</th>
                                <th className="py-1 pr-3">Status</th>
                                <th className="py-1 pr-3 text-right">Amount</th>
                                <th className="py-1 pr-3 text-right">Paid</th>
                                <th className="py-1 pr-3 text-right">Balance</th>
                                <th className="py-1 pr-3 text-right"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.map((f) => (
                                <tr key={f.id} className="border-b border-border/30">
                                  <td className="py-1 pr-3 font-mono">{f.invoice_no || f.id.slice(0, 8)}</td>
                                  <td className="py-1 pr-3 capitalize">{f.module}</td>
                                  <td className="py-1 pr-3">{f.period || f.end_date || "—"}</td>
                                  <td className="py-1 pr-3 capitalize">{f.status}</td>
                                  <td className="py-1 pr-3 text-right font-mono">₹{Number(f.amount).toLocaleString("en-IN")}</td>
                                  <td className="py-1 pr-3 text-right font-mono">₹{Number(f.paid_amount || 0).toLocaleString("en-IN")}</td>
                                  <td className="py-1 pr-3 text-right font-mono font-semibold text-destructive">
                                    ₹{Number(f.balance).toLocaleString("en-IN")}
                                  </td>
                                  <td className="py-1 pr-3 text-right">
                                    {canDeleteFee && (
                                      <Button size="sm" variant="ghost"
                                              onClick={() => setDeletingFee({ ...f, resident_name: r.name })}
                                              data-testid={`delete-outstanding-fee-${f.id}`}
                                              title="Delete this outstanding fee">
                                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">No outstanding balances.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* v42 — Delete outstanding fee confirmation */}
      <Dialog open={!!deletingFee} onOpenChange={(v) => !v && setDeletingFee(null)}>
        <DialogContent data-testid="delete-outstanding-fee-dialog">
          <DialogHeader>
            <DialogTitle>Delete Outstanding Fee?</DialogTitle>
            <DialogDescription>
              {deletingFee && (
                <>
                  {deletingFee.resident_name} · Invoice {deletingFee.invoice_no || deletingFee.id.slice(0, 8)} ·
                  {" "}₹{Number(deletingFee.amount).toLocaleString("en-IN")}
                  {" "}({deletingFee.module} · {deletingFee.period || deletingFee.end_date || "—"}).
                  <br /><br />
                  The fee is soft-deleted: it immediately disappears from Outstanding Dues, the resident
                  ledger, Dashboard dues and every fee module. Linked payments stay preserved for audit.
                  Restorable later from Fees → archived filters if needed.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingFee(null)}>Cancel</Button>
            <Button variant="destructive" disabled={busy} onClick={confirmDeleteFee}
                    data-testid="confirm-delete-outstanding-fee">
              {busy ? "Deleting…" : "Delete Fee"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Expiry Tab ============
function ExpiryTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/reports/subscription-expiry").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const groups = [
    { key: "expired", label: "Expired", tone: "warn" },
    { key: "d7", label: "0-7 days", tone: "warn" },
    { key: "d15", label: "8-15 days", tone: "primary" },
    { key: "d30", label: "16-30 days", tone: "primary" },
  ];

  const chartData = data ? groups.map((g) => ({
    label: g.label,
    count: data[g.key]?.length || 0,
  })) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-sm bg-secondary p-3 rounded-md border border-border">
        <AlertTriangle className="w-4 h-4 mt-0.5 text-primary" />
        <div>
          Refreshing this tab also queues a WhatsApp reminder for every resident whose subscription
          is expired or expiring within 30 days (log-only stub — visible in Notifications).
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {groups.map((g) => (
          <Card key={g.key} className="p-5 border-border bg-white">
            <div className={`eyebrow ${g.tone === "warn" ? "text-destructive" : "text-primary"}`}>{g.label}</div>
            <div className="font-display text-4xl font-bold mt-2">{data?.[g.key]?.length ?? 0}</div>
            <div className="text-xs text-muted-foreground mt-1">residents</div>
          </Card>
        ))}
      </div>

      <Card className="p-6 border-border bg-white">
        <div className="font-display text-xl font-bold mb-4">By bucket</div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E1E7DD" />
              <XAxis dataKey="label" stroke="#5B6B62" fontSize={12} />
              <YAxis stroke="#5B6B62" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0B4A3F", border: "none", color: "#F7F9F6", borderRadius: 6, fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#C45B2D" strokeWidth={2}
                    dot={{ fill: "#C45B2D", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {groups.map((g) => (
        <Card key={g.key} className="p-4 border-border bg-white">
          <div className="flex items-center gap-2 mb-3">
            <LineIcon className="w-4 h-4 text-primary" />
            <div className="font-display font-bold">{g.label} <span className="text-muted-foreground text-sm ml-2">({data?.[g.key]?.length ?? 0})</span></div>
          </div>
          <div className="space-y-1">
            {(data?.[g.key] || []).map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b border-border/50 py-2 text-sm">
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {r.phone || "—"} • Room {r.room_no || "—"} • ends {r.subscription_end}
                  </div>
                </div>
                <Badge variant={r.days_left < 0 ? "destructive" : "secondary"}>
                  {r.days_left < 0 ? `${Math.abs(r.days_left)}d overdue` : `${r.days_left}d left`}
                </Badge>
              </div>
            ))}
            {(!data?.[g.key] || data[g.key].length === 0) && (
              <div className="text-sm text-muted-foreground py-3">None.</div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
